import { Spacing } from '@persona-id/design-tokens';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import FormSectionTranslationsConfig from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionTranslationsConfig';
import { MissingTranslationsMap } from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionTranslationsConfig/FormSectionTranslationsConfig';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';

const FOOTER_HEIGHT = 56;

const TranslationsModalContainer = styled(ModalContainer)`
  height: clamp(800px, 80vh, 1300px);
  width: clamp(800px, 80vw, 1400px);
  padding: 0;
`;

const StyledModalHeader = styled(ModalHeader)`
  padding-top: ${Spacing[4]};
  padding-left: ${Spacing[9]};
  padding-right: ${Spacing[9]};
`;

const StyledModalBody = styled(ModalBody)`
  padding-left: ${Spacing[9]};
  padding-right: ${Spacing[9]};
  padding-top: 0px;
`;

const ModalFooter = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  margin-top: auto;
  height: ${FOOTER_HEIGHT}px;
  padding: ${Spacing[4]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
`;

const Subtitle = styled(Paragraph).attrs(() => ({ size: 'lg' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
  margin-bottom: ${Spacing[3]};
  white-space: pre-wrap;
`;

export interface MissingTranslationsModalProps {
  inquiryTemplateVersion: InquiryTemplateVersion;
  missingTranslations: MissingTranslationsMap;
  disableSave: boolean;
  handleClose: () => void;
  handleSave: () => void;
}

export const MissingTranslationsModal = ({
  inquiryTemplateVersion,
  missingTranslations,
  disableSave,
  handleClose,
  handleSave,
}: MissingTranslationsModalProps) => {
  const configureLink = `/dashboard/inquiry-templates/${inquiryTemplateVersion.relationships['inquiry-template'].data.id}/${inquiryTemplateVersion.id}/config`;
  return (
    <TranslationsModalContainer size='xl'>
      <StyledModalHeader>
        <>
          Add Missing Translations
          <Spacer height={1} />
          <Subtitle>
            Browse and add missing translations found in your template. You can also reduce missing
            translations by removing supported languages in{' '}
            <Link
              id='missing-translations__configure-link'
              to={configureLink}
              onClick={handleClose}
            >
              Config Editor
            </Link>
            .
          </Subtitle>
        </>
      </StyledModalHeader>
      <StyledModalBody>
        <FormSectionTranslationsConfig
          inquiryTemplateVersion={inquiryTemplateVersion}
          missingTranslations={missingTranslations}
        />
      </StyledModalBody>
      <ModalFooter>
        <HStack equal={false}>
          <Button
            id='missing-translations__cancel-button'
            variant='basic'
            aria-label='Cancel'
            onClick={handleClose}
          >
            Dismiss
          </Button>
          <Button
            id='missing-translations__save-button'
            data-cy='missing-translations__save-button'
            variant='primary'
            aria-label='Save'
            onClick={handleSave}
            disabled={disableSave}
          >
            Save
          </Button>
        </HStack>
      </ModalFooter>
    </TranslationsModalContainer>
  );
};
